import {
  ExerciseGroupedData,
  ExerciseMetricGroupedData,
  SectionGroupedData,
  SessionGroupedData,
  SetGroupedData,
  WorkoutGroupedData,
  WorkoutNameGroupedData,
} from '../types';

// Assumption here is that all custom context menu options will match one of these patterns
// If not true, probably add a new pattern but TBD
export type ContextMenuPattern =
  | {
      type: 'oneEntity';
      workoutId: string;
      data:
        | SessionGroupedData
        | SetGroupedData
        | ExerciseGroupedData
        | SectionGroupedData
        | ExerciseMetricGroupedData
        | WorkoutNameGroupedData;
    }
  | {
      type: 'oneWorkout';
      entityType: 'Set';
      workoutId: string;
      sets: SetGroupedData[];
    }
  | {
      type: 'oneWorkout';
      entityType: 'Exercise';
      workoutId: string;
      exercises: ExerciseGroupedData[];
    }
  | {
      type: 'oneWorkout';
      entityType: 'Section';
      workoutId: string;
      sections: SectionGroupedData[];
    }
  | {
      type: 'oneWorkout';
      entityType: 'ExerciseMetric';
      workoutId: string;
      exerciseMetrics: ExerciseMetricGroupedData[];
    }
  | {
      // Only sets allow cross-workout operations (Save as Scheme) for now, but could easily expand this
      type: 'manySets';
      sets: WithWorkoutId<SetGroupedData>[];
    }
  | {
      // Multiple workouts selected - allows bulk clearing
      type: 'manyWorkouts';
      workouts: WithWorkoutId<SessionGroupedData>[];
    }
  | {
      // Sections selected across multiple workouts - allows saving a
      // multi-day template of just the highlighted sections
      type: 'manyWorkoutSections';
      sections: WithWorkoutId<SectionGroupedData>[];
    };

export type WithWorkoutId<T> = {
  workoutId: string;
  data: T;
};

export function processDataForContextMenu(
  groupedData: WorkoutGroupedData[],
): ContextMenuPattern | null {
  const workouts: WithWorkoutId<SessionGroupedData>[] = [];
  const sets: WithWorkoutId<SetGroupedData>[] = [];
  const exerciseMetrics: WithWorkoutId<ExerciseMetricGroupedData>[] = [];
  const exercises: WithWorkoutId<ExerciseGroupedData>[] = [];
  const sections: WithWorkoutId<SectionGroupedData>[] = [];
  const workoutNames: WithWorkoutId<WorkoutNameGroupedData>[] = [];
  let foundType:
    | 'Workout'
    | 'Set'
    | 'Exercise'
    | 'Section'
    | 'ExerciseMetric'
    | 'WorkoutName'
    | false
    | undefined = undefined;
  let foundWorkoutId: string | false | undefined = undefined;

  for (const { workoutId, data } of groupedData) {
    if (foundWorkoutId === undefined) {
      foundWorkoutId = workoutId;
    } else if (workoutId !== foundWorkoutId) {
      foundWorkoutId = false;
    }

    for (const workoutData of data) {
      if (
        workoutData.type === 'AddSection' ||
        workoutData.type === 'AddExercise' ||
        workoutData.type === 'AddSet'
      ) {
        continue;
      }

      if (foundType === undefined) {
        foundType = workoutData.type;
      } else if (workoutData.type !== foundType) {
        foundType = false;
      }

      if (workoutData.type === 'Workout') {
        workouts.push({ data: workoutData, workoutId });
      } else if (workoutData.type === 'Set') {
        sets.push({ data: workoutData, workoutId });
      } else if (workoutData.type === 'Exercise') {
        exercises.push({ data: workoutData, workoutId });
      } else if (workoutData.type === 'Section') {
        sections.push({ data: workoutData, workoutId });
      } else if (workoutData.type === 'WorkoutName') {
        workoutNames.push({ data: workoutData, workoutId });
      } else {
        exerciseMetrics.push({ data: workoutData, workoutId });
      }
    }
  }

  if (foundType === 'Workout') {
    if (workouts.length === 1 && workouts[0]) {
      return {
        type: 'oneEntity',
        workoutId: workouts[0].workoutId,
        data: workouts[0].data,
      };
    } else if (workouts.length > 1) {
      return {
        type: 'manyWorkouts',
        workouts,
      };
    }
  } else if (foundType === 'WorkoutName') {
    if (workoutNames.length === 1 && workoutNames[0]) {
      return {
        type: 'oneEntity',
        workoutId: workoutNames[0].workoutId,
        data: workoutNames[0].data,
      };
    }
  } else if (foundType === 'Set') {
    if (sets.length === 1 && sets[0]) {
      return {
        type: 'oneEntity',
        workoutId: sets[0].workoutId,
        data: sets[0].data,
      };
    } else if (foundWorkoutId) {
      return {
        type: 'oneWorkout',
        entityType: 'Set',
        workoutId: foundWorkoutId,
        sets: sets.map((s) => s.data),
      };
    }
    return {
      type: 'manySets',
      sets,
    };
  } else if (foundType === 'Exercise') {
    if (exercises.length === 1 && exercises[0]) {
      return {
        type: 'oneEntity',
        workoutId: exercises[0].workoutId,
        data: exercises[0].data,
      };
    } else if (foundWorkoutId) {
      return {
        type: 'oneWorkout',
        entityType: 'Exercise',
        workoutId: foundWorkoutId,
        exercises: exercises.map((s) => s.data),
      };
    }
  } else if (foundType === 'Section') {
    if (sections.length === 1 && sections[0]) {
      return {
        type: 'oneEntity',
        workoutId: sections[0].workoutId,
        data: sections[0].data,
      };
    } else if (foundWorkoutId) {
      return {
        type: 'oneWorkout',
        entityType: 'Section',
        workoutId: foundWorkoutId,
        sections: sections.map((s) => s.data),
      };
    }
    // sections spanning multiple workouts — multi-day template save
    return {
      type: 'manyWorkoutSections',
      sections,
    };
  } else if (foundType === 'ExerciseMetric') {
    if (exerciseMetrics.length === 1 && exerciseMetrics[0]) {
      return {
        type: 'oneEntity',
        workoutId: exerciseMetrics[0].workoutId,
        data: exerciseMetrics[0].data,
      };
    } else if (foundWorkoutId) {
      return {
        type: 'oneWorkout',
        entityType: 'ExerciseMetric',
        workoutId: foundWorkoutId,
        exerciseMetrics: exerciseMetrics.map((s) => s.data),
      };
    }
  }

  return null;
}
